import { useEffect, useState } from "react";
import {ICircuitBreaker} from "../types/ICircuitBreaker";
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { GetCircuitBreakerStatus, EnableCircuitBreaker, DisableCircuitBreaker } from "../services/CircuitBreakerService";

interface CircuitBreakerConfig {
    id: string;
    name: string;
    description: string;
}

const circuitBreakers: CircuitBreakerConfig[] = [
    { id: "aqilla", name: "AQilla", description: "Controls the status of the AQilla integration" },
    { id: "axius-maximus", name: "Ask Idwal AI", description: "Controls the status of the 'Ask Idwal AI' capability" },
    { id: "sonarcloud", name: "SonarCloud", description: "Control SonarCloud integration status" },
];

const CircuitBreakersPage = () => {
    const [breakers, setBreakers] = useState<Record<string, ICircuitBreaker>>({});
    const [loaded, setLoaded] = useState<boolean>(false);
    const [error, setError] = useState<string|null>(null);
    const [updating, setUpdating] = useState<Record<string, boolean>>({});
    
    const loadData = async () => {
        setLoaded(false);
        setError(null);
        try {
            const results: Record<string, ICircuitBreaker> = {};
            for (const config of circuitBreakers) {
                results[config.id] = await GetCircuitBreakerStatus(config.id);
            }
            setBreakers(results);
        } catch (err) {
            setError("Failed to load circuit breaker status");
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    async function updateCircuitBreaker(id: string, newVal: boolean) {
        setUpdating(prev => ({ ...prev, [id]: true }));
        try {
            const data = newVal 
                ? await EnableCircuitBreaker(id)
                : await DisableCircuitBreaker(id);
            setBreakers(prev => ({ ...prev, [id]: data }));
            setError(null);
        } catch (err) {
            setError(`Failed to ${newVal ? 'enable' : 'disable'} ${id} circuit breaker`);
        } finally {
            setUpdating(prev => ({ ...prev, [id]: false }));
        }
    }

    const setValue = (id: string, val: string) => {
        if (val) {
            updateCircuitBreaker(id, val === "Enabled");
        }
    }

    if (!loaded) {
        return <div className="flex justify-content-center align-items-center h-20rem">Loading...</div>
    }

    if (error) {
        return (
            <div className="flex flex-column justify-content-center align-items-center h-20rem gap-3">
                <div className="text-red-500">{error}</div>
                <Button label="Retry" onClick={loadData} />
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-2xl font-semibold mb-4">Circuit Breakers</h2>
            <div className="flex flex-column gap-3">
                {circuitBreakers.map(config => (
                    <div key={config.id} className="card p-4 shadow-2">
                        <div className="flex justify-content-between align-items-center">
                            <div>
                                <h3 className="text-lg font-medium mb-1">{config.name}</h3>
                                <p className="text-600 text-sm">{config.description}</p>
                            </div>
                            <SelectButton 
                                value={breakers[config.id]?.enabled ? "Enabled" : "Disabled"} 
                                onChange={(e) => setValue(config.id, e.value)} 
                                options={["Enabled", "Disabled"]}
                                disabled={updating[config.id]}
                            />
                        </div>
                        <div>Status message {breakers[config.id]?.message}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CircuitBreakersPage;
